import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Home, Send, Calendar } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';

type ContactFormValues = {
  name: string;
  email: string;
  phone: string;
  company?: string;
  message: string;
};

const Contact = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);
  const calendlyRef = useRef<HTMLDivElement>(null);

  const contactSchema = z.object({
    name: z.string()
      .trim()
      .min(2, { message: t('contact.page.validation.nameMin') })
      .max(100, { message: t('contact.page.validation.nameMax') }),
    email: z.string()
      .trim()
      .email({ message: t('contact.page.validation.emailInvalid') })
      .max(255, { message: t('contact.page.validation.emailMax') }),
    phone: z.string()
      .trim()
      .min(9, { message: t('contact.page.validation.phoneMin') })
      .max(20, { message: t('contact.page.validation.phoneMax') }),
    company: z.string()
      .trim()
      .max(100, { message: t('contact.page.validation.companyMax') })
      .optional(),
    message: z.string()
      .trim()
      .min(10, { message: t('contact.page.validation.messageMin') })
      .max(1000, { message: t('contact.page.validation.messageMax') }),
  });

  // Debug environment variables
  console.log('=== CONTACT COMPONENT LOADED ===');
  console.log('Supabase URL:', import.meta.env.VITE_SUPABASE_URL);
  console.log('Supabase Key exists:', !!import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY);

  useEffect(() => {
    // Add Google Ads conversion tracking script immediately
    const adsScript = document.createElement('script');
    adsScript.innerHTML = `
      function gtag_report_conversion(url) {
        var callback = function () {
          if (typeof(url) != 'undefined') {
            window.location = url;
          }
        };
        gtag('event', 'conversion', {
            'send_to': 'AW-17675770920/x4kSCKq9kLMbEKi4u-xB',
            'event_callback': callback
        });
        return false;
      }
    `;
    document.head.appendChild(adsScript);

    // Lazy load Calendly only when widget is visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !calendlyLoaded) {
            setCalendlyLoaded(true);
            
            // Load Calendly script when visible
            const calendlyScript = document.createElement('script');
            calendlyScript.src = 'https://assets.calendly.com/assets/external/widget.js';
            calendlyScript.async = true;
            document.body.appendChild(calendlyScript);
          }
        });
      },
      { rootMargin: '100px' } // Start loading 100px before element is visible
    );

    if (calendlyRef.current) {
      observer.observe(calendlyRef.current);
    }

    return () => {
      if (calendlyRef.current) {
        observer.unobserve(calendlyRef.current);
      }
      if (document.head.contains(adsScript)) {
        document.head.removeChild(adsScript);
      }
    };
  }, [calendlyLoaded]);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      message: '',
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    console.log('=== FORM SUBMIT STARTED ===');
    console.log('Form data received:', data);
    setIsSubmitting(true);
    
    try {
      // Push GTM consent event
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: "gtm.init_consent",
          "gtm.uniqueEventId": 1
        });
      }

      console.log('About to invoke supabase function...');
      
      const result = await supabase.functions.invoke('send-contact-email', {
        body: data,
      });

      console.log('Full result:', result);
      console.log('Result data:', result.data);
      console.log('Result error:', result.error);

      if (result.error) {
        console.error('Supabase function returned error:', result.error);
        throw result.error;
      }

      console.log('Email sent successfully!');

      // Track conversion
      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
        (window as any).gtag_report_conversion();
      }

      toast({
        title: t('contact.page.successTitle'),
        description: t('contact.page.successDesc'),
      });

      form.reset();
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      toast({
        title: t('contact.page.errorTitle'),
        description: t('contact.page.errorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>{t('contact.page.title')}</title>
        <meta name="description" content={t('contact.page.description')} />
        <meta name="keywords" content="contacto, orçamento, marketing digital, websites Portugal, SEO Portugal" />
        <link rel="canonical" href="https://spawncore.com/contact" />
        <meta name="robots" content="index, follow" />
        
        <meta property="og:title" content={t('contact.page.title')} />
        <meta property="og:description" content={t('contact.page.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spawncore.com/contact" />
      </Helmet>

      <Navigation />

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
            <Link to="/" className="hover:text-primary smooth-transition flex items-center gap-1">
              <Home className="h-4 w-4" />
              {t('nav.home')}
            </Link>
            <span>/</span>
            <span className="text-foreground">{t('contact.page.breadcrumb')}</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-12"
          >
            {/* Calendly Section */}
            <div className="space-y-6">
              <div className="text-center">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-primary" />
                <h2 className="text-3xl font-bold text-foreground mb-2">
                  {t('contact.bookMeeting')}
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  {t('contact.bookMeetingSubtitle')}
                </p>
              </div>
              <div
                ref={calendlyRef}
                className="calendly-inline-widget rounded-xl w-full overflow-hidden mx-auto relative"
                data-url="https://calendly.com/joao-spawncore/30min?hide_gdpr_banner=1&hide_event_type_details=1&background_color=0f151c&text_color=ffffff&primary_color=175cd9&embed_type=Inline"
                style={{ minWidth: '0', width: '100%', height: '700px' }}
              >
                {!calendlyLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-card/50 backdrop-blur-sm">
                    <div className="text-center space-y-4">
                      <Calendar className="h-12 w-12 mx-auto text-primary animate-pulse" />
                      <p className="text-muted-foreground">{t('contact.loading') || 'A carregar calendário...'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Form Section */}
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-4 text-center">
                {t('contact.page.orSendMessage')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 text-center">
                {t('contact.page.formSubtitle')}
              </p>

            <div className="minimal-card p-5 sm:p-8 rounded-xl glow-border">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
                  console.log('=== FORM VALIDATION FAILED ===');
                  console.log('Validation errors:', errors);
                })} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input placeholder={`${t('contact.name')} ${t('contact.page.required')}`} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input type="email" placeholder={`${t('contact.email')} ${t('contact.page.required')}`} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input placeholder={`${t('contact.phone')} ${t('contact.page.required')}`} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input placeholder={`${t('contact.company')} ${t('contact.page.optional')}`} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea 
                            placeholder={`${t('contact.message')} ${t('contact.page.required')}`}
                            className="min-h-[150px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={isSubmitting}
                    onClick={() => console.log('Button clicked! isSubmitting:', isSubmitting)}
                  >
                    {isSubmitting ? (
                      t('contact.sending')
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        {t('contact.send')}
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </div>

              <div className="mt-8 text-center">
                <p className="text-muted-foreground mb-2">{t('contact.page.orContactDirect')}</p>
                <a 
                  href="mailto:joao@spawncore.com"
                  className="text-primary hover:text-primary-dark smooth-transition text-lg font-semibold"
                >
                  joao@spawncore.com
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
